import React, { useEffect, useMemo, useRef, useState } from "react"
import { Card, Button, Space, Flex, Tooltip } from "antd"
import TextArea from "antd/es/input/TextArea"
import {
  CloseOutlined,
  ExpandOutlined,
  ShrinkOutlined,
  DeleteOutlined,
  HistoryOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  LoadingOutlined,
  PaperClipOutlined
} from "@ant-design/icons"
import classNames from "classnames"
import dayjs from "dayjs"
import moment from "moment"
import PropTypes from "prop-types"
import { useSelector, useDispatch } from "react-redux"
import { closeChatCard } from "../../../store/aiassistant"
import { useAssistantPersona } from "hooks/useAssistantPersona"
import styles from "./ChatBox.module.css"
import { Text } from "ui/Typography"
import SystemPrompt from "./SystemPrompt"
import AssistantAvatar from "./AssistantAvatar"
import { SendPlane } from "ui/Icon/DSIcon/SendPlane"
import { GridMenu } from "ui/Icon/DSIcon/GridMenu"
import { renderAIMessage, renderOptionButtons, navOptionsOf, MAIN_MENU_OPTION } from "./utils"
import { useTranslation } from "react-i18next"
import { useChatSession } from "./useChatSession"
import AddCardSection from "./AddCardSection"
import { spacings } from "themes/variables"
import { formatDayLabel, DAY_LABEL_TODAY, DAY_LABEL_YESTERDAY } from "helpers/time"

const ThinkingDots = () => (
  <span className={styles.thinkingDots}>
    <span className={styles.dot}></span>
    <span className={styles.dot}></span>
    <span className={styles.dot}></span>
  </span>
)

// Stable grouping keys for the history panel, separate from their displayed labels so the
// grouping logic never carries raw English. Today/Yesterday reuse the same sentinels as the
// day-divider code (helpers/time) for consistency; the other two buckets have no divider
// equivalent, so they get their own stable keys.
const HISTORY_GROUP_THIS_WEEK = "this_week"
const HISTORY_GROUP_OLDER = "older"

const HISTORY_GROUP_LABEL_KEYS = {
  [DAY_LABEL_TODAY]: "ai_assistant.period_today",
  [DAY_LABEL_YESTERDAY]: "ai_assistant.period_yesterday",
  [HISTORY_GROUP_THIS_WEEK]: "ai_assistant.period_this_week",
  [HISTORY_GROUP_OLDER]: "ai_assistant.period_older"
}

function groupSessionsByDate(sessions) {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const yesterdayStart = todayStart - 86400000
  // moment-based subtraction rather than `todayStart - 6 * 86400000`: a day isn't always
  // exactly 86,400,000ms across a DST transition, so raw ms arithmetic drifts by an hour.
  const weekStart = moment(todayStart).subtract(6, "days").valueOf()

  return sessions.reduce(
    (groups, s) => {
      const ts = s.timestamp || 0
      if (ts >= todayStart) groups[DAY_LABEL_TODAY].push(s)
      else if (ts >= yesterdayStart) groups[DAY_LABEL_YESTERDAY].push(s)
      else if (ts >= weekStart) groups[HISTORY_GROUP_THIS_WEEK].push(s)
      else groups[HISTORY_GROUP_OLDER].push(s)
      return groups
    },
    { [DAY_LABEL_TODAY]: [], [DAY_LABEL_YESTERDAY]: [], [HISTORY_GROUP_THIS_WEEK]: [], [HISTORY_GROUP_OLDER]: [] }
  )
}

const HistoryPanel = ({ sessions, isLoadingHistory, deletingSessionId, onLoad, onDelete, onClose }) => {
  const { t } = useTranslation()
  const groups = groupSessionsByDate(sessions)

  return (
    <div className={styles.historyPanel}>
      <Flex align="center" gap={spacings.S} className={styles.historyHeader}>
        <Button type="text" icon={<ArrowLeftOutlined />} aria-label={t("ai_assistant.back")} onClick={onClose} />
        <Text fw="600" className={styles.historyTitle}>
          {t("ai_assistant.chat_history_title")}
        </Text>
      </Flex>

      <div className={styles.historyList}>
        {isLoadingHistory && (
          <div className={styles.historyEmpty}>
            <LoadingOutlined />
          </div>
        )}
        {!isLoadingHistory && sessions.length === 0 && (
          <div className={styles.historyEmpty}>{t("ai_assistant.no_previous_conversations")}</div>
        )}
        {!isLoadingHistory &&
          Object.entries(groups).map(([label, items]) =>
            items.length === 0 ? null : (
              <div key={label}>
                <div className={styles.historyGroupLabel}>{t(HISTORY_GROUP_LABEL_KEYS[label])}</div>
                {items.map((s) => (
                  <div key={s.sessionId} className={styles.historyItem}>
                    <button className={styles.historyItemBtn} onClick={() => onLoad(s)}>
                      <span className={styles.historyItemTitle}>{s.title}</span>
                    </button>
                    <Tooltip title={t("ai_assistant.delete")}>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={deletingSessionId === s.sessionId}
                        disabled={deletingSessionId !== null}
                        aria-label={t("ai_assistant.delete_session")}
                        className={styles.historyDeleteBtn}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(s.sessionId)
                        }}
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
            )
          )}
      </div>
    </div>
  )
}

HistoryPanel.propTypes = {
  sessions: PropTypes.array.isRequired,
  isLoadingHistory: PropTypes.bool.isRequired,
  deletingSessionId: PropTypes.string,
  onLoad: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

// Matches the design's "01:02AM". Returns null when a message carries no time (history
// loaded from a service that did not send one) so nothing is rendered rather than a guess.
const formatMessageTime = (createdAt) => (createdAt ? dayjs(createdAt).format("hh:mmA") : null)

// The day-separator label above the first message of each calendar day —
// "Today" / "Yesterday" / the weekday name for the rest of the last week /
// "September 13" beyond that (shared with the Messages page's day dividers
// via helpers/time so the two chat UIs can't disagree on format). Returns
// null for a message with no timestamp, same reasoning as formatMessageTime
// above.
const formatDayDivider = (createdAt, t) => {
  if (!createdAt) return null
  const label = formatDayLabel(createdAt)
  if (label === DAY_LABEL_TODAY) return t("ai_assistant.period_today")
  if (label === DAY_LABEL_YESTERDAY) return t("ai_assistant.period_yesterday")
  return label
}

const RECEIPT_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,application/pdf"

const ChatBox = () => {
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false)
  const [selectedPrompt, setSelectedPrompt] = useState(null)
  const [isHistoryVisible, setIsHistoryVisible] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [message, setMessage] = useState("")
  const [attachedFile, setAttachedFile] = useState(null)
  const [attachedPreview, setAttachedPreview] = useState(null)
  const fileInputRef = useRef(null)
  const scrollAnchorRef = useRef(null)
  const dispatch = useDispatch()
  const isOpen = useSelector(({ chatCard }) => chatCard.isOpen)
  const persona = useAssistantPersona()
  const { t } = useTranslation()
  const personaName = t(persona.nameKey)

  const {
    sessionId,
    chatHistory,
    sessions,
    isLoading,
    isLoadingHistory,
    deletingSessionId,
    startNewSession,
    sendMessage,
    submitCard,
    loadSessions,
    loadSession,
    deleteSession,
    markOptionSelected
  } = useChatSession()

  // Precomputed once per chatHistory change rather than inside the render map, where
  // reduce() would otherwise re-scan the whole history for every message rendered.
  const lastAiIdx = useMemo(() => chatHistory.reduce((last, m, i) => (m.role === "ai" ? i : last), -1), [chatHistory])

  // Day-divider flags computed once per chatHistory change, not recomputed per render
  // inside an IIFE — each entry says whether a divider goes above that message.
  const chatHistoryWithDividers = useMemo(() => {
    let lastDayLabel = null
    return chatHistory.map((msg) => {
      const dayLabel = formatDayDivider(msg.createdAt, t)
      const showDayDivider = dayLabel !== null && dayLabel !== lastDayLabel
      if (showDayDivider) lastDayLabel = dayLabel
      return { ...msg, dayLabel, showDayDivider }
    })
  }, [chatHistory, t])

  const lastAiMsg = [...chatHistory].reverse().find((m) => m.role === "ai")
  const fileUploadRequired = !!lastAiMsg?.fileUpload
  const addCard = lastAiMsg?.addCard || null

  // Only the newest reply's navigation is actionable, so the footer shows that one.
  // "Open Main Menu" drops out while the menu is open — it is already showing.
  const navBarOptions = (lastAiMsg ? navOptionsOf(lastAiMsg) : []).filter(
    (opt) => !(isMainMenuOpen && opt === MAIN_MENU_OPTION)
  )
  // On an empty chat the default prompts are already the whole screen, so opening
  // the menu must not repeat them down here.
  const showMainMenuPrompts = isMainMenuOpen && chatHistory.length > 0

  useEffect(() => {
    if (isOpen && !sessionId) startNewSession()
  }, [isOpen, sessionId])

  useEffect(() => {
    if (chatHistory.length > 0 || isLoading || isMainMenuOpen) {
      scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [chatHistory, isLoading, isMainMenuOpen])

  const clearAttachment = () => {
    if (attachedPreview) URL.revokeObjectURL(attachedPreview)
    setAttachedFile(null)
    setAttachedPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (attachedPreview) URL.revokeObjectURL(attachedPreview)
    setAttachedFile(file)
    setAttachedPreview(file.type === "application/pdf" ? null : URL.createObjectURL(file))
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleSendMessage = async (text) => {
    const query = (text ?? message).trim()
    if (!query && !attachedFile) return
    // The send button stays enabled at all times (by design), so the "one request at
    // a time" rule lives here instead. Without it a send during a history load is
    // wiped by the arriving transcript, and two in flight at once answer each other's
    // questions out of order.
    if (isLoading) return
    const fileToSend = attachedFile
    setMessage("")
    setIsMainMenuOpen(false)
    setSelectedPrompt(null)
    clearAttachment()
    await sendMessage(query, fileToSend)
  }

  const handleNewChat = () => {
    startNewSession()
    setMessage("")
    clearAttachment()
    setIsMainMenuOpen(false)
    setSelectedPrompt(null)
    setIsHistoryVisible(false)
  }

  const handleShowHistory = () => {
    loadSessions()
    setIsHistoryVisible(true)
  }

  const handleClose = () => {
    dispatch(closeChatCard())
    setMessage("")
    clearAttachment()
  }

  return (
    isOpen && (
      <Card
        title={
          <Flex className={styles.headerSpace}>
            <Flex align="center" gap={spacings.S} className={styles.headerTitle}>
              <AssistantAvatar persona={persona} size={32} className={styles.headerAvatar} />
              <Flex vertical gap={0}>
                <Text fw="700" className={styles.personaName}>{personaName}</Text>
                <Text variant="caption" className={styles.personaSubtitle}>{t("ai_assistant.subtitle")}</Text>
              </Flex>
            </Flex>
            <Space>
              <Tooltip title={t("ai_assistant.new_chat")}>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  aria-label={t("ai_assistant.new_chat")}
                  className={styles.headerAction}
                  onClick={handleNewChat}
                />
              </Tooltip>
              <Tooltip title={t("ai_assistant.chat_history")}>
                <Button
                  type="text"
                  icon={<HistoryOutlined />}
                  aria-label={t("ai_assistant.chat_history")}
                  aria-pressed={isHistoryVisible}
                  className={classNames(styles.headerAction, isHistoryVisible && styles.headerActionActive)}
                  onClick={handleShowHistory}
                />
              </Tooltip>
              <span className={styles.expandBtn}>
                {isFullscreen ? (
                  <Button
                    type="text"
                    icon={<ShrinkOutlined />}
                    className={styles.headerAction}
                    aria-label={t("ai_assistant.shrink")}
                    onClick={() => setIsFullscreen(false)}
                  />
                ) : (
                  <Button
                    type="text"
                    icon={<ExpandOutlined />}
                    className={styles.headerAction}
                    aria-label={t("ai_assistant.expand")}
                    onClick={() => setIsFullscreen(true)}
                  />
                )}
              </span>
              <Button
                type="text"
                icon={<CloseOutlined />}
                aria-label={t("ai_assistant.close")}
                className={styles.headerAction}
                onClick={handleClose}
              />
            </Space>
          </Flex>
        }
        className={classNames(styles.chatCard, isFullscreen && styles.fullscreen)}
        styles={{ body: { padding: 0 } }}
      >
        {isHistoryVisible && (
          <HistoryPanel
            sessions={sessions}
            isLoadingHistory={isLoadingHistory}
            deletingSessionId={deletingSessionId}
            onLoad={(session) => {
              loadSession(session)
              setIsHistoryVisible(false)
            }}
            onDelete={(sid) => deleteSession(sid, sessionId, handleNewChat)}
            onClose={() => setIsHistoryVisible(false)}
          />
        )}

        <div className={styles.chatBody}>
          <Flex flex={1} />
          <Flex vertical gap={spacings.M}>
            <Flex vertical gap={spacings.L} role="log" aria-live="polite" aria-label="Chat messages">
              {chatHistory.length === 0 && (
                <SystemPrompt
                  selectedPrompt={selectedPrompt}
                  handlePromptClick={(title) => {
                    setSelectedPrompt(title)
                    handleSendMessage(title)
                  }}
                />
              )}
              {chatHistoryWithDividers.map((msg, idx) => {
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.showDayDivider && (
                        <Flex justify="center">
                          <Text className={styles.dayDivider}>{msg.dayLabel}</Text>
                        </Flex>
                      )}
                      {msg.role === "user" ? (
                        <Flex justify="end" className={classNames("mr-s", styles.userMessage)}>
                          <Flex vertical align="end" gap={spacings.XXS}>
                            {msg.filePreview && (
                              <img src={msg.filePreview} alt="" className={styles.messageImage} />
                            )}
                            {msg.content && <Text className={styles.aiMessage}>{msg.content}</Text>}
                            {formatMessageTime(msg.createdAt) && (
                              <Text className={styles.messageTime}>{formatMessageTime(msg.createdAt)}</Text>
                            )}
                          </Flex>
                        </Flex>
                      ) : (
                        <Flex vertical align="start" gap={spacings.XXS}>
                          <div className={styles.aiMessage}>
                            {renderAIMessage(msg, (opt) => {
                              // Opening the menu asks the assistant nothing, so it
                              // neither sends a message nor marks the row answered —
                              // Step Back beside it stays clickable.
                              if (opt === MAIN_MENU_OPTION) {
                                setIsMainMenuOpen(true)
                                return
                              }
                              markOptionSelected(msg.id, opt)
                              handleSendMessage(opt)
                            }, idx === lastAiIdx)}
                          </div>
                          {formatMessageTime(msg.createdAt) && (
                            <Text className={styles.messageTime}>{formatMessageTime(msg.createdAt)}</Text>
                          )}
                        </Flex>
                      )}
                    </React.Fragment>
                  )
                })}

              {isLoading && (
                <Flex gap={spacings.M} align="center">
                  <ThinkingDots />
                </Flex>
              )}
              <div ref={scrollAnchorRef} />
            </Flex>
          </Flex>
        </div>

        <div className={styles.footer}>
          {/* Navigation lives here, not in the message: a full-width divider between
              the chat and a centred row of nav buttons, directly above the composer
              (Figma). The main menu, when opened, fills the same area. */}
          {(navBarOptions.length > 0 || showMainMenuPrompts) && (
            <div className={styles.navBar}>
              {showMainMenuPrompts && (
                <SystemPrompt
                  selectedPrompt={selectedPrompt}
                  showGreeting={false}
                  handlePromptClick={(title) => {
                    setSelectedPrompt(title)
                    handleSendMessage(title)
                  }}
                />
              )}
              {navBarOptions.length > 0 && renderOptionButtons(navBarOptions, "nav", "bar", (opt) => {
                if (opt === MAIN_MENU_OPTION) {
                  setIsMainMenuOpen(true)
                  return
                }
                if (lastAiMsg) markOptionSelected(lastAiMsg.id, opt)
                handleSendMessage(opt)
              }, null, isLoading, styles.optionButtonsCentered)}
            </div>
          )}
          {addCard && (
            <div className={styles.fileUploadArea}>
              <AddCardSection disabled={isLoading} onSubmitted={(paymentMethodId) => submitCard(addCard, paymentMethodId)} />
            </div>
          )}
          {fileUploadRequired && (
            <div className={styles.fileUploadArea}>
              <input
                ref={fileInputRef}
                type="file"
                accept={RECEIPT_ACCEPT}
                className={styles.hiddenFileInput}
                onChange={handleFileChange}
              />
              {attachedFile ? (
                <div className={styles.attachmentPreview}>
                  {attachedPreview
                    ? <img src={attachedPreview} alt="attachment" className={styles.attachmentThumb} />
                    : <div className={styles.attachmentThumb}><PaperClipOutlined /> {attachedFile.name}</div>
                  }
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    className={styles.attachmentRemove}
                    aria-label="Remove attachment"
                    onClick={clearAttachment}
                  />
                </div>
              ) : (
                <button className={styles.fileUploadTrigger} disabled={isLoading} onClick={() => fileInputRef.current?.click()}>
                  <PaperClipOutlined />
                  <span>{t("ai_assistant.upload_receipt")}</span>
                </button>
              )}
            </div>
          )}
          <Flex align="center" gap={spacings.M}>
            <Flex align="center" className={styles.composerBar}>
              <TextArea
                placeholder={t("ai_assistant.placeholder_ask_something_else")}
                aria-label={t("ai_assistant.placeholder_ask_something_else")}
                className={styles.inputBox}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault()
                    handleSendMessage()
                  }
                }}
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
              <span className={styles.inputDivider} />
              <Button
                type="text"
                icon={<GridMenu width={18} height={18} />}
                aria-label={t("ai_assistant.main_menu")}
                aria-pressed={isMainMenuOpen}
                className={styles.mainMenuButton}
                disabled={isLoading}
                onClick={() => setIsMainMenuOpen((open) => !open)}
              />
            </Flex>
            <Button
              type="primary"
              icon={<SendPlane />}
              aria-label={t("ai_assistant.send_message")}
              className={styles.sendButton}
              onClick={() => handleSendMessage()}
            />
          </Flex>
        </div>
      </Card>
    )
  )
}

export default ChatBox
